import React from 'react';
import { AlertCircle } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
}

export const Input: React.FC<InputProps> = ({
    label,
    error,
    leftIcon,
    rightIcon,
    fullWidth = true,
    className = '',
    id,
    ...props
}) => {
    const inputId = id || props.name || Math.random().toString(36).substr(2, 9);

    return (
        <div className={`${fullWidth ? 'w-full' : ''}`}>
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-xs font-medium uppercase tracking-wider text-white/40 mb-2 ml-0.5"
                >
                    {label}
                </label>
            )}

            <div className="relative group">
                {leftIcon && (
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/25 transition-colors duration-300 group-focus-within:text-brand pointer-events-none z-20">
                        {leftIcon}
                    </div>
                )}

                <input
                    id={inputId}
                    className={`
                        w-full bg-white/[0.03] backdrop-blur-sm
                        border border-white/[0.06] rounded-xl
                        px-4 py-3 text-white
                        placeholder:text-white/20
                        focus:bg-white/[0.05] focus:border-brand/30
                        focus:shadow-[0_0_0_3px_rgba(255,153,28,0.08)]
                        focus:outline-none
                        transition-all duration-300
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${leftIcon ? 'pl-11' : ''}
                        ${rightIcon ? 'pr-11' : ''}
                        ${error ? 'border-danger/40 shadow-[0_0_0_3px_rgba(239,68,68,0.08)] focus:border-danger/60' : ''}
                        ${className}
                    `}
                    {...props}
                />

                {rightIcon && !error && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/25 transition-colors duration-300 group-focus-within:text-brand pointer-events-none z-20">
                        {rightIcon}
                    </div>
                )}

                {error && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-danger z-20 pointer-events-none">
                        <AlertCircle className="w-4 h-4" />
                    </div>
                )}
            </div>

            {error && (
                <div className="flex items-center mt-2 ml-1 text-danger text-xs animate-fade-in">
                    <AlertCircle className="w-3 h-3 mr-1.5" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};
